/**
 * Module dependencies.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useProjects } from 'app/hooks/requests/projects/use-projects';
import { ProjectRole } from 'app/types/project';

/**
 * Export `useProjectRole`.
 *
 * Returns the role for the current project based on URL params and projects list.
 */

export function useProjectRole(): ProjectRole | undefined {
  const { projectId } = useParams();
  const { data: projects } = useProjects();

  return useMemo(() => {
    if (!projectId || !projects) {
      return undefined;
    }

    const project = projects.find(p => p.id === parseInt(projectId, 10));

    return project?.role ?? 'owner';
  }, [projectId, projects]);
}

/**
 * Export `useIsProjectReadOnly`.
 *
 * Returns true if the current user is a viewer (read-only) on this project.
 */

export function useIsProjectReadOnly(): boolean {
  const role = useProjectRole();

  return role === 'viewer';
}

/**
 * Export `useCanEditProject`.
 *
 * Returns true if the current user can edit (owner or editor).
 */

export function useCanEditProject(): boolean {
  const role = useProjectRole();

  return !role || role === 'owner' || role === 'editor';
}
